import { RefObject, useEffect, useState } from 'react';
import { DETAIL_COLUMN_LAYOUTS } from '../../../../constants';

export type DetailColumnLayout = {
  count: number;
  narrow: number;
  wide: number;
};

const getLayout = (width: number): DetailColumnLayout =>
  DETAIL_COLUMN_LAYOUTS.find(({ minWidth }) => width >= minWidth)!.layout;

export const useDetailColumnLayout = (
  ref: RefObject<HTMLElement | null>,
  dependency: unknown
): DetailColumnLayout => {
  const [layout, setLayout] = useState<DetailColumnLayout>(() =>
    getLayout(window.innerWidth)
  );

  // `dependency` re-runs the effect once the grid mounts (it renders only after data loads).
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Column count follows the container, not the window: the content box is always
    // narrower, so window width over-commits columns near a breakpoint and they clip.
    const check = () => {
      const next = getLayout(el.clientWidth);
      // count uniquely identifies a layout -> keep the same reference across no-op resizes
      setLayout((prev) => (prev.count === next.count ? prev : next));
    };

    check();
    const observer = new ResizeObserver(() => requestAnimationFrame(check));
    observer.observe(el);

    return () => observer.disconnect();
  }, [ref, dependency]);

  return layout;
};
